import React, { useState, Dispatch, useEffect, useRef } from "react";
import RectImg from "./rect-img.png";
import AddIcon from "./add-icon.png";
import { IoClose } from "react-icons/io5";
import { CommonButton } from "../../../common-styles";
import { Upload } from "antd";
import UploadGallery from "../../../Assets/Images/UploadGallery.svg";
import { Spinner, TextAreaCount } from "../../../Modals/styled";
import type { UploadProps } from "antd";
import * as API from "../../../API/path";
import toast from "react-hot-toast";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../Redux/store";
import { createNewPost } from "../../../Redux/slices/postSlice";
import * as Interface from "../../../Config/interface.config";
import { isCreatorRole, isFileVideo } from "../../../Config/common.config";
import FanxoPlayBtnImg from "../../../Assets/Images/fanxo-play-btn.png";
import VideoPlayer from "../VideoHandler";
import { getImageUrl } from "../../../Utils/helpers";
import DefaultUserImg from "../../../Assets/Images/user-profile.png";
import { getUserDetails } from "../../../Redux/slices/userSlice";

import type { UploadFile } from "antd/es/upload/interface";

interface Props {
  fileList: { fileURL: string; fileId: string }[];
  setFileList: Dispatch<
    React.SetStateAction<{ fileURL: string; fileId: string }[]>
  >;
  step: number;
  setStep: Dispatch<React.SetStateAction<number>>;
  onHide: () => void;
  isIntro?: boolean;
}

const StepOne = ({
  fileList,
  setFileList,
  setStep,
  onHide,
  isIntro,
}: Props) => {
  const user = useSelector((state: RootState) => state.user);
  const { id: user_id } = user;
  const dispatch: AppDispatch = useDispatch();
  const approvedCreator = isCreatorRole(user.role) && user.isApproved === true;

  const [selectedImage, setSelectedImage] = useState<string>("");
  const [rawFileList, setRawFileList] = useState<UploadFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileUploadRef: any = useRef();

  interface NewPostFormData {
    description: string;
  }

  const initialValues: NewPostFormData = {
    description: "",
  };

  const validationSchema = Yup.object().shape({
    description: Yup.string().max(
      2000,
      "Caption cannot be more than 2000 characters"
    ),
  });

  const [disableFileUpload, setDisableFileUpload] = useState<boolean>(false);

  const props: UploadProps = {
    name: "file",
    multiple: true,
    beforeUpload: (file) => {
      const supportedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "video/mp4", "video/quicktime"];
      const isSupported = supportedTypes.includes(file.type);
      if (!isSupported) {
        toast.error("The uploaded file format is not supported. Please upload a JPG, PNG, WEBP, GIF, MP4, or MOV file.");
        return Upload.LIST_IGNORE;
      }

      setRawFileList((prev) => [...prev, file]);

      const reader = new FileReader();
      reader.onload = (e) => {
        const fileURL = e.target?.result as string;
        setFileList((prev) => [...prev, { fileURL, fileId: file.uid }]);
      };
      reader.readAsDataURL(file);

      return false; // Prevent automatic upload
    },
    onRemove: (file) => {
      const newRawFiles = rawFileList.filter((f) => f.uid !== file.uid);
      setRawFileList(newRawFiles);

      const newFiles = fileList.filter((f) => f.fileId !== file.uid);
      setFileList(newFiles);
    },
  };

  /** ---------------------------------------------------------
   *   ACTUAL FIX — PROPER UPLOAD HANDLER
   * ---------------------------------------------------------*/
  const uploadFilesToServer = async () => {
    const uploads = rawFileList.map(async (file) => {
      const formData = new FormData();
      formData.append("file", file as any);
      formData.append("uploadType", "post-image");

      const res = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}${API.fileUpload.url}`,
        {
          method: "POST",
          body: formData,
          headers: {
            authorization: "authorization-text",
          },
        }
      );

      // Stop on HTTP status error (413, 500, etc)
      if (!res.ok) {
        let msg = "File upload failed";
        try {
          const errJson = await res.json();
          msg = errJson?.message || msg;
        } catch {}

        throw new Error(msg);
      }

      const data = await res.json();

      if (data.error) {
        throw new Error(data.message || "Failed to upload file");
      }

      return data;
    });

    return Promise.all(uploads);
  };

  /** ---------------------------------------------------------------
   *   Submit post — only after all files upload successfully
   * ---------------------------------------------------------------*/
  const handleSubmit = async (data: NewPostFormData) => {
    if (!approvedCreator) {
      toast.error("Your account is not approved yet.");
      return;
    }

    if (!user_id) {
      toast.error("Invalid user.");
      return;
    }

    setIsSubmitting(true);

    try {
      /** Upload files */
      const uploadResults = await uploadFilesToServer();
      const uploadedFileIds = uploadResults.map((result) => result._id);

      /** Create post only if uploads succeeded */
      const postData: Interface.Post = {
        text: data.description,
        creator: user_id,
        files: uploadedFileIds,
        likeCount: [],
        comments: [],
        tips: [],
        isIntro: isIntro,
      };

      await dispatch(createNewPost(postData));
      onHide();

      dispatch(getUserDetails());
    } catch (err: any) {
      toast.error(err.message || "File upload failed");
      setIsSubmitting(false);
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const removePostImage = (image: string) => {
    setFileList(fileList.filter((file) => file.fileURL !== image));
  };

  useEffect(() => {
    if (fileList.length > 0) {
      const exists = fileList.find((f) => f.fileURL === selectedImage);
      if (!exists) setSelectedImage(fileList[0].fileURL);
    } else {
      setSelectedImage("");
    }
  }, [fileList, selectedImage]);

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values }) => (
          <Form>
            {/* Upload UI */}
            <div className={`${fileList.length > 0 ? "hidden" : "block"}`}>
              <Upload.Dragger
                {...props}
                height={450}
                showUploadList={false}
                className="block upload-modal"
                disabled={disableFileUpload}
              >
                <img
                  src={UploadGallery}
                  width={120}
                  className="mx-auto"
                  alt="fanxo"
                />
                <p
                  ref={fileUploadRef}
                  className="ant-upload-text mt-0 !text-white"
                >
                  Drag and Photos videos here
                </p>
                <div className="mt-10 max-w-[350px] mx-auto">
                  <CommonButton type="button" variant="tertary">
                    Select From Device
                  </CommonButton>
                </div>
              </Upload.Dragger>

              <div className="mt-5">
                <CommonButton
                  type="button"
                  variant="tertary"
                  style={{ maxWidth: "350px", margin: "auto" }}
                  className="select-text-only-button"
                  onClick={() => setStep(2)}
                  onMouseEnter={() => setDisableFileUpload(true)}
                  onMouseLeave={() => setDisableFileUpload(false)}
                >
                  Select Text Only Post
                </CommonButton>
              </div>
            </div>

            {fileList.length > 0 && (
              <>
                <div className="relative w-full md:flex bg-black block">
                  <div className="relative flex-1">
                    {isFileVideo(selectedImage) ? (
                      <div className="w-full object-cover h-[700px] relative z-0">
                        <VideoPlayer locked={false} url={selectedImage} />
                      </div>
                    ) : (
                      <img
                        alt="uploaded-file"
                        src={selectedImage}
                        className="w-full object-cover h-[400px] md:h-[700px]"
                      />
                    )}

                    <div className="flex gap-0 flex-wrap items-center absolute w-[95%] mx-auto z-10 left-1/2 bottom-10 -translate-x-1/2">
                      {fileList.map((img) => (
                        <div className="w-28 h-28 relative" key={img.fileId}>
                          <div
                            className="w-full h-full absolute bg-black opacity-50"
                            onClick={() => setSelectedImage(img.fileURL)}
                          ></div>
                          <img
                            alt="uploaded-file"
                            src={
                              isFileVideo(img.fileURL)
                                ? FanxoPlayBtnImg
                                : img.fileURL
                            }
                            className="w-full h-full object-cover"
                          />
                          <IoClose
                            onClick={() => removePostImage(img.fileURL)}
                            color="#fff"
                            size={25}
                            strokeWidth={0.5}
                            className="absolute top-1 right-1 cursor-pointer"
                          />
                        </div>
                      ))}

                      <div
                        onClick={() => fileUploadRef?.current.click()}
                        className="w-28 h-28 bg-black relative opacity-50 flex items-center justify-center cursor-pointer"
                      >
                        <img
                          src={RectImg}
                          className="opacity-65 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                        />
                        <img
                          src={AddIcon}
                          className="opacity-65 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Caption */}
                  <div className="md:max-w-[35%] md:w-[400px] bg-black px-5 pb-10">
                    <div className="gap-4 items-center mt-4 relative">
                      <div className="flex gap-5 items-center">
                        <img
                          src={getImageUrl(user.image?.fileURL ?? "") || DefaultUserImg}
                          alt="fanxo user"
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <h5 className="text-white text-base font-inter">
                          {user.fullname ?? "User Fullname"}
                        </h5>
                      </div>

                      <Field
                        rows={4}
                        as="textarea"
                        name="description"
                        className="w-full mt-3 text-lg py-2 bg-[#000000] text-white rounded resize-none placeholder:text-white/70"
                        placeholder="Caption it..."
                      />

                      <TextAreaCount className="text-right font-inter">
                        {values.description.length}/2000
                      </TextAreaCount>
                      <ErrorMessage
                        name="description"
                        component="p"
                        className="text-red-600 mt-2"
                      />

                      <CommonButton
                        type="submit"
                        variant="tertary"
                        className="w-full mt-5"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? <Spinner /> : "Share"}
                      </CommonButton>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default StepOne;
